import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from 'lib/api';
import type { QueryConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import type { PinterestCampaignEntry } from './types';

export interface GetPinterestCampaignsParams {
  accountId: string;
}

export const getPinterestCampaigns = async (
  { params }: { params: GetPinterestCampaignsParams },
  signal?: AbortSignal,
): Promise<PinterestCampaignEntry[]> => {
  const res = await apiClient.get<PinterestCampaignEntry[]>(
    `${queryKeys.campaigns}/${params.accountId}/`,
    { signal },
  );

  if (isAxiosError(res)) {
    const error = new Error('Get Pinterest Campaigns Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }

  return res.data;
};

interface UsePinterestCampaignsOptions<TData> {
  params: GetPinterestCampaignsParams;
  config?: QueryConfig<typeof getPinterestCampaigns, TData>;
}

export const usePinterestCampaigns = <TData = PinterestCampaignEntry[]>({
  params,
  config,
}: UsePinterestCampaignsOptions<TData>) =>
  useQuery({
    staleTime: 0,
    refetchOnMount: true,
    retry: false,
    ...config,
    enabled: (config?.enabled ?? true) && !!params.accountId,
    queryFn: ({ signal }) => getPinterestCampaigns({ params }, signal),
    queryKey: [queryKeys.campaigns, params.accountId],
  });
